import { useEffect } from "react";
import { Link } from "react-router-dom";
import Lenis from "lenis";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { Reveal, Chapter } from "../components/v2/shared";
import { Nav } from "../components/v2/Nav";
import { CircadianDial } from "../components/science/CircadianDial";
import { HormoneCurves } from "../components/science/HormoneCurves";
import { ChronotypeBars } from "../components/science/ChronotypeBars";
import { DecisionWindow } from "../components/science/DecisionWindow";
import { TransitForecast } from "../components/science/TransitForecast";

const record = [
  {
    src: "Nobel Prize \u00B7 2017",
    line: "The molecular machinery that runs a 24 hour clock inside almost every cell.",
    href: "https://www.nobelprize.org/prizes/medicine/2017/press-release/",
  },
  {
    src: "Current Biology \u00B7 2013",
    line: "One week of natural light shifted the human clock earlier by nearly two hours.",
    href: "https://pubmed.ncbi.nlm.nih.gov/23910656/",
  },
  {
    src: "J Clin Endocrinol Metab \u00B7 2011",
    line: "Ordinary evening room light suppresses melatonin and delays its onset.",
    href: "https://pubmed.ncbi.nlm.nih.gov/21193540/",
  },
  {
    src: "Stress \u00B7 2004",
    line: "Cortisol rises sharply in the first 30 minutes after you wake.",
    href: "https://pubmed.ncbi.nlm.nih.gov/15062637/",
  },
  {
    src: "Current Biology \u00B7 2004",
    line: "Chronotype spreads peak timing across the population by several hours.",
    href: "https://www.cell.com/current-biology/fulltext/S0960-9822(04)00928-5",
  },
  {
    src: "Current Biology \u00B7 2012",
    line: "Social jetlag, living against your own clock, tracks with worse health outcomes.",
    href: "https://pubmed.ncbi.nlm.nih.gov/22578422/",
  },
  {
    src: "Cognitive Neuropsychology \u00B7 2007",
    line: "Attention, memory and executive function all vary with time of day.",
    href: "https://pubmed.ncbi.nlm.nih.gov/18568951/",
  },
  {
    src: "PNAS \u00B7 2011",
    line: "Favourable rulings fell steadily across a session and reset after a break.",
    href: "https://www.pnas.org/doi/10.1073/pnas.1018033108",
  },
  {
    src: "Journal of Neuroscience \u00B7 2011",
    line: "Sleep loss changes how the brain weighs risk against reward.",
    href: "https://www.jneurosci.org/content/31/10/3712",
  },
];

const layers = [
  {
    label: "MEASURED",
    tag: "Can be checked",
    body: "The 24 hour clock. The light response. Cortisol and melatonin curves. Chronotype. Sleep debt. Decision fatigue. Peer reviewed, replicated, and linked below.",
  },
  {
    label: "MAPPED",
    tag: "A model, not a law",
    body: "How we turn your baseline and your day into one score, a peak and a dip. It is consistent and it is useful. It is ours, and it is not a published finding.",
  },
  {
    label: "READ",
    tag: "Where we can be wrong",
    body: "What a day means for a conversation, an ask, a recovery week. Interpretation on top of the model. This is where we speak, and we say so.",
  },
];

const method = [
  "Set your baseline from your birth date, time and place.",
  "Read today against that baseline, on your device.",
  "Score the day. Mark the peak and the dip.",
  "Label every output: measured, mapped or read.",
  "Hand you the hours. The call stays yours.",
];

const Stat = ({ value, label, delay = 0 }) => (
  <Reveal delay={delay}>
    <div className="border-t border-[#F2F0EB]/10 pt-4">
      <p className="h-heavy text-4xl sm:text-5xl text-[#FF4D0A]">{value}</p>
      <p className="font-mono-brand text-[10px] tracking-[0.2em] uppercase text-[#6E7178] mt-2 leading-relaxed">{label}</p>
    </div>
  </Reveal>
);

const Sources = ({ children }) => (
  <p className="font-mono-brand text-[10px] tracking-[0.2em] uppercase text-[#6E7178] mt-8">
    Sources: {children}
  </p>
);

const MonoStack = ({ lines, testId }) => (
  <div className="mt-10 max-w-lg border-b border-[#F2F0EB]/10" data-testid={testId}>
    {lines.map((l, i) => (
      <div key={l} className="flex gap-5 items-baseline border-t border-[#F2F0EB]/10 py-3.5">
        <span className="font-mono-brand text-[10px] text-[#FF4D0A] shrink-0">/{String(i + 1).padStart(2, "0")}</span>
        <p className="font-display font-bold text-sm sm:text-base text-[#F2F0EB] leading-snug">{l}</p>
      </div>
    ))}
  </div>
);

export default function Science() {
  useEffect(() => {
    window.scrollTo(0, 0);
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    const raf = (time) => {
      lenis.raf(time);
      requestAnimationFrame(raf);
    };
    requestAnimationFrame(raf);
    return () => lenis.destroy();
  }, []);

  return (
    <div className="grain bg-[#0B0B0C] text-[#F2F0EB] min-h-screen" data-testid="science-page">
      <Nav />

      {/* Hero */}
      <section className="relative overflow-hidden pt-24 sm:pt-32 pb-20 sm:pb-28">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse 55% 45% at 70% 20%, rgba(255,77,10,0.10), transparent 70%)" }}
        />
        <div className="relative mx-auto max-w-6xl px-5 sm:px-8">
          <p className="eyebrow mb-8" data-testid="science-hero-eyebrow">The science</p>
          <h1 className="h-heavy text-5xl sm:text-7xl lg:text-8xl leading-[0.92]" data-testid="science-hero-headline">
            <span className="block">You are not moody.</span>
            <span className="block text-[#FF4D0A]">You are on a clock.</span>
          </h1>
          <Reveal delay={0.35}>
            <p className="mt-8 text-base sm:text-lg text-[#9DA1A8] leading-relaxed max-w-xl" data-testid="science-hero-sub">
              Your body keeps 24 hour time whether you track it or not. Light,
              cortisol, melatonin, temperature, attention. Here is what is
              measured, what we build on top of it, and exactly where our read
              begins.
            </p>
          </Reveal>
          <Reveal delay={0.45}>
            <MonoStack
              testId="science-hero-stack"
              lines={[
                "We start from the clock.",
                "We map where you sit on it.",
                "We say so when the data ends and the read begins.",
              ]}
            />
          </Reveal>
          <Reveal delay={0.55}>
            <div className="mt-12 inline-flex items-center gap-2 text-[#6E7178] font-mono-brand text-[10px] tracking-[0.25em] uppercase">
              The record, unpacked <ArrowDown size={12} />
            </div>
          </Reveal>
        </div>
      </section>

      {/* 01 The clock */}
      <section className="py-16 sm:py-24 border-t border-[#F2F0EB]/5" data-testid="science-ch-clock">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="lg:order-2">
            <Reveal><Chapter num="01" label="The clock · 24 hours" /></Reveal>
            <Reveal>
              <h2 className="h-heavy text-[32px] sm:text-5xl leading-[0.95]">
                Every cell you have
                <br />
                <span className="text-[#FF4D0A]">keeps time.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-7 text-[#9DA1A8] leading-relaxed max-w-lg">
                A master clock in the hypothalamus drives cortisol, melatonin,
                core temperature, blood pressure and reaction time on a roughly
                24 hour loop. The 2017 Nobel Prize in Physiology or Medicine
                went to the scientists who found the genes that run it.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 text-[#9DA1A8] leading-relaxed max-w-lg">
                That clock is why 9am you and 4pm you are not the same person,
                and why the same conversation lands differently six hours
                apart. Nothing about that is mystical. It is measured.
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <p className="mt-6 text-sm text-[#6E7178] leading-relaxed max-w-lg" data-testid="science-clock-honest-line">
                This chapter is textbook biology. Everything we build sits on
                top of it, in that order.
              </p>
            </Reveal>
            <div className="mt-10 grid grid-cols-2 gap-6 max-w-md">
              <Stat value="2017" label="Nobel Prize in Medicine" delay={0.1} />
              <Stat value="24H" label="The cycle your cells run on" delay={0.2} />
            </div>
            <Sources>Nobel Committee, Karolinska Institutet</Sources>
          </div>
          <Reveal delay={0.2} className="lg:order-1"><CircadianDial /></Reveal>
        </div>
      </section>

      {/* 02 The light */}
      <section className="py-16 sm:py-24 border-t border-[#F2F0EB]/5" data-testid="science-ch-light">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <Reveal><Chapter num="02" label="The light · the dose" /></Reveal>
            <Reveal>
              <h2 className="h-heavy text-[32px] sm:text-5xl leading-[0.95]">
                Light sets the clock.
                <br />
                <span className="text-[#FF4D0A]">Nothing else comes close.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-7 text-[#9DA1A8] leading-relaxed max-w-lg">
                Morning light pulls your clock earlier. Evening light pushes it
                later. Cortisol spikes within half an hour of waking, melatonin
                starts climbing a couple of hours before sleep, and ordinary
                room light at 11pm is enough to blunt it.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 text-[#F2F0EB] leading-relaxed max-w-lg font-display font-bold" data-testid="science-light-bold-line">
                Your schedule is not neutral. It is a light exposure plan you
                never wrote down.
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <p className="mt-6 text-[#9DA1A8] leading-relaxed max-w-lg">
                Season and latitude change the dose, which is why the place and
                the time of year you were born into form part of your baseline,
                and why the same week feels different in January and in June.
              </p>
            </Reveal>
            <Sources>J Clin Endocrinol Metab &middot; Current Biology</Sources>
          </div>
          <Reveal delay={0.2}><HormoneCurves /></Reveal>
        </div>
      </section>

      {/* 03 The chronotype */}
      <section className="py-16 sm:py-24 border-t border-[#F2F0EB]/5" data-testid="science-ch-chronotype">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="lg:order-2">
            <Reveal><Chapter num="03" label="The chronotype · your offset" /></Reveal>
            <Reveal>
              <h2 className="h-heavy text-[32px] sm:text-5xl leading-[0.95]">
                Not everybody
                <br />
                <span className="text-[#FF4D0A]">peaks at nine.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-7 text-[#9DA1A8] leading-relaxed max-w-lg">
                Everyone runs the same 24 hour clock, offset. Early types are
                sharpest before 11. Late types do not properly arrive until
                the afternoon. The gap between the two extremes is measured in
                hours, not minutes.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 text-[#9DA1A8] leading-relaxed max-w-lg">
                Most calendars are built for one chronotype and handed to
                everybody. That mismatch has a name, social jetlag, and it is
                why the 9am meeting feels free to one person and expensive to
                another.
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <p className="mt-6 text-sm text-[#6E7178] leading-relaxed max-w-lg" data-testid="science-chronotype-honest-line">
                Population ranges, not a diagnosis. Your own offset is yours.
              </p>
            </Reveal>
            <Sources>Current Biology &middot; Munich ChronoType research</Sources>
          </div>
          <Reveal delay={0.2} className="lg:order-1"><ChronotypeBars /></Reveal>
        </div>
      </section>

      {/* 04 The cost */}
      <section className="py-16 sm:py-24 border-t border-[#F2F0EB]/5" data-testid="science-ch-cost">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <Reveal><Chapter num="04" label="The cost · decision fatigue" /></Reveal>
            <Reveal>
              <h2 className="h-heavy text-[32px] sm:text-5xl leading-[0.95]">
                The same decision
                <br />
                <span className="text-[#FF4D0A]">costs more at 4pm.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-7 text-[#9DA1A8] leading-relaxed max-w-lg">
                In one of the most cited studies on the subject, favourable
                rulings dropped steadily across a session and jumped back up
                after a break. Same people. Same cases. Different hour.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 text-[#9DA1A8] leading-relaxed max-w-lg">
                Add short sleep and the brain starts weighing risk and reward
                differently. You do not feel less capable. You just quietly
                decide worse.
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <p className="mt-6 text-[#F2F0EB] leading-relaxed max-w-lg font-display font-bold" data-testid="science-cost-bold-line">
                Timing is not a soft variable. It shows up in the outcome.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <MonoStack
                testId="science-cost-stack"
                lines={[
                  "Put the ask in your peak.",
                  "Put the hard talk after a reset.",
                  "Put nothing important in your dip.",
                ]}
              />
            </Reveal>
            <Sources>PNAS 2011 &middot; Journal of Neuroscience 2011</Sources>
          </div>
          <Reveal delay={0.2}><DecisionWindow /></Reveal>
        </div>
      </section>

      {/* 05 The week */}
      <section className="py-16 sm:py-24 border-t border-[#F2F0EB]/5" data-testid="science-ch-week">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="lg:order-2">
            <Reveal><Chapter num="05" label="The week · conditions" /></Reveal>
            <Reveal>
              <h2 className="h-heavy text-[32px] sm:text-5xl leading-[0.95]">
                Closer to a forecast
                <br />
                <span className="text-[#FF4D0A]">than a prophecy.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-7 text-[#9DA1A8] leading-relaxed max-w-lg">
                The product is not what will happen. The product is what kind
                of week you are walking into. Some days you have the energy to
                push. Some days you have the energy to repair, and that is all.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 text-[#9DA1A8] leading-relaxed max-w-lg">
                When we say a window is hard, we mean your capacity is low and
                friction is likely. When we say a window is open, we mean the
                opposite. You decide what to do with either.
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <p className="mt-6 text-[#F2F0EB] leading-relaxed max-w-lg font-display font-bold" data-testid="science-week-bold-line">
                You still steer. The forecast does not take the wheel.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.2} className="lg:order-1"><TransitForecast /></Reveal>
        </div>
      </section>

      {/* 06 The line */}
      <section className="py-16 sm:py-24 border-t border-[#F2F0EB]/5" data-testid="science-ch-line">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <Reveal><Chapter num="06" label="The line · where the science ends" /></Reveal>
          <Reveal>
            <h2 className="h-heavy text-[32px] sm:text-5xl leading-[0.95] max-w-3xl">
              Three layers.
              <br />
              <span className="text-[#FF4D0A]">Clearly labelled.</span>
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-px bg-[#F2F0EB]/10 border border-[#F2F0EB]/10">
            {layers.map((l, i) => (
              <Reveal key={l.label} delay={0.1 + i * 0.12} className="bg-[#0B0B0C] p-6 sm:p-8" data-testid={`science-layer-${l.label.toLowerCase()}`}>
                <p className="font-mono-brand text-[11px] tracking-[0.28em] text-[#FF4D0A]">{l.label}</p>
                <p className="font-mono-brand text-[9px] tracking-[0.18em] uppercase text-[#6E7178] mt-2">{l.tag}</p>
                <p className="mt-5 text-sm text-[#9DA1A8] leading-relaxed">{l.body}</p>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-10 text-[#F2F0EB] leading-relaxed max-w-2xl font-display font-bold" data-testid="science-line-rule">
              If a line cannot be traced back to a measurement, it is a read.
              If a read is written as a fact, we failed.
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <p className="mt-14 text-2xl sm:text-3xl h-heavy leading-snug max-w-3xl" data-testid="science-closing-line">
              The work is not to make a daily read look like a lab.
              <span className="text-[#6E7178]"> It is to stop mixing the two in the same sentence.</span>
            </p>
          </Reveal>
        </div>
      </section>

      {/* 07 The receipts */}
      <section className="py-16 sm:py-24 border-t border-[#F2F0EB]/5" data-testid="science-receipts">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <Reveal><Chapter num="07" label="The receipts" /></Reveal>
          <Reveal>
            <h2 className="h-heavy text-[32px] sm:text-5xl leading-[0.95]">
              Read it yourself.
            </h2>
          </Reveal>
          <div className="mt-10 border-b border-[#F2F0EB]/10">
            {record.map((r, i) => (
              <Reveal key={r.src + r.line} delay={0.04 * i}>
                <a
                  href={r.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex flex-col sm:flex-row sm:items-baseline gap-1.5 sm:gap-6 border-t border-[#F2F0EB]/10 py-4 sm:py-5"
                  data-testid={`science-record-${i + 1}`}
                >
                  <span className="font-mono-brand text-[10px] tracking-[0.2em] uppercase text-[#FF4D0A] shrink-0 sm:w-64">{r.src}</span>
                  <span className="flex-1 text-sm sm:text-base text-[#9DA1A8] group-hover:text-[#F2F0EB] transition-colors leading-relaxed">{r.line}</span>
                  <ArrowUpRight size={14} className="hidden sm:block self-center text-[#6E7178] group-hover:text-[#FF4D0A] transition-colors shrink-0" />
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* The method */}
      <section className="py-16 sm:py-24 border-t border-[#F2F0EB]/5" data-testid="science-method">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <Reveal><Chapter label="The method" /></Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px bg-[#F2F0EB]/10 border border-[#F2F0EB]/10">
            {method.map((line, i) => (
              <Reveal key={line} delay={0.08 + i * 0.08} className="bg-[#0B0B0C] p-6 sm:p-7" data-testid={`science-method-step-${i + 1}`}>
                <span className="h-heavy text-4xl sm:text-5xl text-[#FF4D0A]">{i + 1}</span>
                <p className="mt-4 text-sm text-[#9DA1A8] leading-relaxed">{line}</p>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.3}>
            <p className="mt-8 font-mono-brand text-[11px] tracking-[0.2em] uppercase text-[#FF4D0A]" data-testid="science-method-line">
              Measured / mapped / read. Labelled, every time.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="py-20 sm:py-28 border-t border-[#F2F0EB]/5 relative overflow-hidden" data-testid="science-cta-section">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse 50% 50% at 50% 100%, rgba(255,77,10,0.10), transparent 70%)" }}
        />
        <div className="relative mx-auto max-w-6xl px-5 sm:px-8 text-center">
          <Reveal>
            <h2 className="h-heavy text-4xl sm:text-6xl leading-[0.95]">
              Stop guessing your energy.
              <br />
              <span className="text-[#FF4D0A]">Start using your clock.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-10">
              <a
                href="/#secure"
                className="inline-flex items-center justify-center gap-2 bg-[#FF4D0A] hover:bg-[#FF6A2E] transition-colors px-8 sm:px-10 py-4 sm:py-5 font-mono-brand text-[11px] sm:text-xs font-bold tracking-[2px] uppercase text-[#0B0B0C]"
                data-testid="science-cta-secure"
              >
                Create my Source Code
                <ArrowUpRight size={14} strokeWidth={2.5} />
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.25}>
            <p className="mt-6 font-mono-brand text-[10px] tracking-[0.2em] uppercase text-[#6E7178]">
              Two minutes. Computed on your device.
            </p>
          </Reveal>
        </div>
      </section>

      <footer className="border-t border-[#F2F0EB]/5">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 py-8 flex items-center justify-between">
          <Link
            to="/"
            className="text-xs sm:text-sm text-[#9DA1A8] hover:text-[#F2F0EB] transition-colors font-mono-brand tracking-widest"
            data-testid="science-footer-home"
          >
            &larr; RETURN HOME
          </Link>
          <span className="text-xs text-[#6E7178] font-mono-brand tracking-widest">
            &copy; 2026 SOURCECODED
          </span>
        </div>
      </footer>
    </div>
  );
}
